import React, { useCallback, useEffect, useState } from "react";
import Spinner from "react-bootstrap/Spinner";
import {
  ArrowClockwise,
  ChevronDown,
  ChevronRight,
} from "react-bootstrap-icons";
import {
  fetchOrganizationBillingOverview,
  type OrgBillingOverview,
  type SuperAdminBillingOverviewFilterParams,
} from "../api/plans";
import {
  listSuperAdminOrganizations,
  type SuperAdminOrganization,
} from "../api/superadmin";
import CustomDropdown from "../components/CustomDropdown";
import "./SuperAdminPlans-new.css";

const STATUS_OPTIONS = [
  { value: "", label: "All Statuses" },
  { value: "succeeded", label: "Succeeded" },
  { value: "pending", label: "Pending" },
  { value: "failed", label: "Failed" },
  { value: "partially_refunded", label: "Partially Refunded" },
  { value: "refunded", label: "Refunded" },
];
const TYPE_OPTIONS = [
  { value: "", label: "All Types" },
  { value: "subscription", label: "Subscription" },
  { value: "initial_recharge", label: "Initial Credits" },
  { value: "auto_recharge", label: "Auto-Recharge" },
  { value: "manual_recharge", label: "Manual Recharge" },
  { value: "overage_debt_settlement_recharge", label: "Overage Debt Settlement" },
];
const date = (value: string | null | undefined): string =>
  value ? new Date(value).toLocaleString() : "-";
const paymentType = (
  type: string | null | undefined,
  recharge: string | null | undefined,
): string =>
  type !== "llm_usage_recharge"
    ? "Subscription"
    : (
        {
          initial_recharge: "Initial Credits",
          auto_recharge: "Auto-Recharge",
          manual_recharge: "Manual Recharge",
          overage_debt_settlement_recharge: "Overage Debt Settlement",
        } as Record<string, string>
      )[recharge || ""] || "LLM Usage Credits";
const paymentFor = (payment: OrgBillingOverview["payments"][number]): string =>
  payment.payment_type === "llm_usage_recharge"
    ? "LLM Usage Credits"
    : payment.plan_name_snapshot || "Subscription plan";
const paymentLabel = (
  payment: OrgBillingOverview["payments"][number],
): string => {
  if (payment.row_kind === "refund")
    return payment.status === "refunded"
      ? "Refund of payment above"
      : "Partial refund of payment above";
  if (payment.row_kind === "related") return "Original payment";
  if (payment.payment_type === "llm_usage_recharge")
    return paymentType(payment.payment_type, payment.recharge_type);
  return (
    (
      {
        initial: "New subscription",
        renewal: "Renewal",
        upgrade: "Upgrade",
        downgrade: "Downgrade",
      } as Record<string, string>
    )[payment.payment_context || ""] || "Subscription payment"
  );
};

export default function SuperAdminPaymentHistory(): React.ReactElement {
  const [organizations, setOrganizations] = useState<SuperAdminOrganization[]>(
    [],
  );
  const [organizationId, setOrganizationId] = useState("");
  const [overview, setOverview] = useState<OrgBillingOverview | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("");
  const [type, setType] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [filters, setFilters] = useState<SuperAdminBillingOverviewFilterParams>(
    {},
  );
  const [expandedRefundPayments, setExpandedRefundPayments] = useState<
    Set<number>
  >(new Set());

  useEffect(() => {
    void listSuperAdminOrganizations({ page_size: 200 })
      .then((response) => setOrganizations(response.items))
      .catch((err: any) =>
        setError(err?.message || "Failed to load organizations"),
      );
  }, []);
  const load = useCallback(async () => {
    if (!organizationId) {
      setOverview(null);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      setOverview(
        await fetchOrganizationBillingOverview(Number(organizationId), {
          paymentLimit: 100,
          usageLimit: 1,
          ...filters,
        }),
      );
    } catch (err: any) {
      setOverview(null);
      setError(
        err?.response?.data?.detail ||
          err?.message ||
          "Failed to load payment history",
      );
    } finally {
      setLoading(false);
    }
  }, [filters, organizationId]);
  useEffect(() => {
    void load();
  }, [load]);
  const apply = (): void =>
    setFilters({
      ...(search.trim() ? { payment_search: search.trim() } : {}),
      ...(status ? { payment_status: status } : {}),
      ...(type ? { payment_type: type } : {}),
      ...(fromDate ? { payment_date_from: fromDate } : {}),
      ...(toDate ? { payment_date_to: toDate } : {}),
    });
  const clear = (): void => {
    setSearch("");
    setStatus("");
    setType("");
    setFromDate("");
    setToDate("");
    setFilters({});
  };
  const organizationOptions = organizations.map((organization) => ({
    value: String(organization.id),
    label: organization.name,
  }));
  const toggleRefundPayment = (paymentId: number): void =>
    setExpandedRefundPayments((current) => {
      const next = new Set(current);
      if (next.has(paymentId)) next.delete(paymentId);
      else next.add(paymentId);
      return next;
    });
  const refundParentIds = new Set(
    (overview?.payments || [])
      .filter((payment) => payment.row_kind === "refund")
      .map((payment) => payment.parent_payment_id || payment.id),
  );
  const visiblePayments = (overview?.payments || []).filter(
    (payment) =>
      !(
        payment.status === "succeeded" &&
        payment.row_kind !== "refund" &&
        refundParentIds.has(payment.id)
      ),
  );

  return (
    <div className="sa-plans-page">
      <div className="sa-plans-breadcrumbs">Super Admin - Payment History</div>
      <div className="sa-plans-header-row">
        <div>
          <h1 className="sa-plans-title">Payment History</h1>
          <p className="sa-plans-subtitle">
            Review billing transactions for an organization.
          </p>
        </div>
        <div className="sa-plans-actions">
          <button
            type="button"
            className="sa-plans-btn sa-plans-btn-primary"
            onClick={() => void load()}
            disabled={loading}
          >
            <ArrowClockwise size={14} /> Refresh
          </button>
        </div>
      </div>
      <div className="sa-plans-card">
        <div className="sa-orgbilling-filters">
          <div className="sa-orgbilling-org-select">
            <CustomDropdown
              value={organizationId}
              options={organizationOptions}
              onChange={setOrganizationId}
              placeholder="Select organization"
            />
          </div>
        </div>
        {organizationId && (
          <div className="sa-orgbilling-filters">
            <input
              className="sa-orgbilling-filter-input"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Search payments..."
            />
            <CustomDropdown
              value={status}
              options={STATUS_OPTIONS}
              onChange={setStatus}
              placeholder="All Statuses"
            />
            <CustomDropdown
              value={type}
              options={TYPE_OPTIONS}
              onChange={setType}
              placeholder="All Types"
            />
            <input
              className="sa-orgbilling-filter-input"
              type="date"
              value={fromDate}
              onChange={(event) => setFromDate(event.target.value)}
            />
            <input
              className="sa-orgbilling-filter-input"
              type="date"
              value={toDate}
              onChange={(event) => setToDate(event.target.value)}
            />
            <button
              type="button"
              className="sa-orgbilling-filter-btn"
              onClick={apply}
            >
              Search
            </button>
            {Object.keys(filters).length > 0 && (
              <button
                type="button"
                className="sa-orgbilling-filter-clear"
                onClick={clear}
              >
                Clear
              </button>
            )}
          </div>
        )}
        {error && <div className="sa-plans-empty">{error}</div>}
        {!organizationId ? (
          <div className="sa-plans-empty">
            Select an organization to view its payment history.
          </div>
        ) : loading ? (
          <div className="sa-plans-empty">
            <Spinner animation="border" size="sm" /> Loading payments...
          </div>
        ) : !overview || visiblePayments.length === 0 ? (
          <div className="sa-plans-empty">No payments recorded.</div>
        ) : (
          <table className="sa-plans-table" data-responsive-cards="true">
            <thead>
              <tr>
                <th>Date</th>
                <th>For</th>
                <th>Type</th>
                <th>Amount</th>
                <th>Status</th>
                <th>Period</th>
                <th>Receipt</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {visiblePayments.map((payment) => {
                const isRefund =
                  payment.row_kind === "refund" ||
                  ["partially_refunded", "refunded"].includes(payment.status);
                const parentPaymentId = payment.parent_payment_id || payment.id;
                const expanded =
                  isRefund && expandedRefundPayments.has(parentPaymentId);
                const original = {
                  ...payment,
                  row_kind: "related" as const,
                  status: "succeeded",
                  amount_usd:
                    (payment.related_payment_amount_cents ||
                      payment.amount_cents) / 100,
                  created_at:
                    payment.related_payment_paid_at ||
                    payment.related_payment_created_at ||
                    payment.created_at,
                  receipt_url: payment.related_payment_receipt_url || null,
                  period_start: payment.related_payment_period_start || null,
                  period_end: payment.related_payment_period_end || null,
                };
                return (
                  <React.Fragment
                    key={`${payment.row_kind || "payment"}-${payment.id}-${payment.attempt_id || "legacy"}`}
                  >
                    <tr className={isRefund ? "sa-payment-refund-row" : ""}>
                      <td data-label="Date">{date(payment.created_at)}</td>
                      <td data-label="For">{paymentFor(payment)}</td>
                      <td data-label="Type">{paymentLabel(payment)}</td>
                      <td data-label="Amount">
                        {payment.amount_usd.toFixed(2)}{" "}
                        {payment.currency.toUpperCase()}
                        {isRefund && (payment.refund_events || []).length > 1 && (
                          <small className="sa-payment-refund-count">{payment.refund_events!.length} refunds</small>
                        )}
                      </td>
                      <td data-label="Status">
                        <span
                          className={`sa-plans-pill ${payment.status === "succeeded" ? "sa-plans-pill-active" : "sa-plans-pill-inactive"}`}
                        >
                          {payment.status.replace(/_/g, " ")}
                        </span>
                      </td>
                      <td data-label="Period">
                        {date(payment.period_start)} -{" "}
                        {date(payment.period_end)}
                      </td>
                      <td data-label="Receipt">
                        {!isRefund &&
                        payment.status === "succeeded" &&
                        payment.receipt_url ? (
                          <a
                            href={payment.receipt_url}
                            target="_blank"
                            rel="noreferrer"
                          >
                            View
                          </a>
                        ) : (
                          "-"
                        )}
                      </td>
                      <td data-label="Actions">
                        {isRefund ? (
                          <button
                            type="button"
                            className="sa-payment-history-expand"
                            onClick={() => toggleRefundPayment(parentPaymentId)}
                            title={
                              expanded
                                ? "Hide original payment"
                                : "Show original payment"
                            }
                          >
                            {expanded ? (
                              <ChevronDown size={16} />
                            ) : (
                              <ChevronRight size={16} />
                            )}
                          </button>
                        ) : (
                          "-"
                        )}
                      </td>
                    </tr>
                    {expanded && (
                      <>
                      {(payment.refund_events || []).length > 1 && (payment.refund_events || []).map((refundEvent) => <tr className="sa-payment-original-row" key={`refund-event-${refundEvent.id}`}><td data-label="Date">{date(refundEvent.created_at)}</td><td data-label="For">{paymentFor(payment)}</td><td data-label="Type">Refund</td><td data-label="Amount">{(refundEvent.amount_cents / 100).toFixed(2)} {payment.currency.toUpperCase()}</td><td data-label="Status"><span className="sa-plans-pill sa-plans-pill-inactive">refunded</span></td><td data-label="Period">-</td><td data-label="Receipt">-</td><td data-label="Actions">-</td></tr>)}
                      <tr className="sa-payment-original-row">
                        <td data-label="Date">{date(original.created_at)}</td>
                        <td data-label="For">{paymentFor(original)}</td>
                        <td data-label="Type">{paymentLabel(original)}</td>
                        <td data-label="Amount">
                          {original.amount_usd.toFixed(2)}{" "}
                          {original.currency.toUpperCase()}
                        </td>
                        <td data-label="Status">
                          <span className="sa-plans-pill sa-plans-pill-active">
                            succeeded
                          </span>
                        </td>
                        <td data-label="Period">
                          {date(original.period_start)} -{" "}
                          {date(original.period_end)}
                        </td>
                        <td data-label="Receipt">
                          {original.receipt_url ? (
                            <a
                              href={original.receipt_url}
                              target="_blank"
                              rel="noreferrer"
                            >
                              View
                            </a>
                          ) : (
                            "-"
                          )}
                        </td>
                        <td data-label="Actions">-</td>
                      </tr>
                      </>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
